import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FolderSelector } from "@/components/folder/FolderSelector";
import { Button } from "@/components/ui/button";
import { useCan } from "@/hooks/useCan";
import { useLogout } from "@/hooks/useLogout";
import { useAuthStore } from "@/stores/auth";
import type { Role } from "@/api/types";

export interface AppShellProps {
  children: ReactNode;
}

/**
 * Top header + (auth-only) sidebar + main + footer. The sidebar is rendered
 * only when a session is present; admin links are gated through `useCan`
 * (UI_PLAN §10.3 — sidebar items by role).
 */
export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.userId);
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      {/*
        TaskMe d68407fd — drop the `mx-auto max-w-6xl` constraint on the
        shell row. The old constraint added equal left/right gutters that
        looked like "empty space left of the sidebar" on wide monitors.
        The sidebar now hugs the viewport edge (modulo `px-4` breathing
        room) and the `main` keeps a readable column via its own max-w.
      */}
      <div className="flex w-full max-w-screen-2xl flex-1 gap-6 px-4 py-6">
        {userId ? <Sidebar /> : null}
        <main className="min-w-0 flex-1">{children}</main>
      </div>
      <footer className="border-t bg-background/95">
        <div className="flex h-10 items-center px-4 text-xs text-muted-foreground">
          {t("app.footer")}
        </div>
      </footer>
    </div>
  );
}

function Header() {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.userId);
  return (
    <header className="border-b bg-background/95">
      <div className="flex h-14 items-center justify-between px-4">
        <Link to="/" className="text-base font-semibold">
          {t("app.title")}
        </Link>
        <nav
          aria-label={t("app.mainMenu")}
          className="flex items-center gap-3 text-sm"
        >
          {/* FolderSelector self-hides for single-folder users, so we can
              always mount it here for any authenticated session. */}
          {userId ? <FolderSelector /> : null}
          <UserChip />
        </nav>
      </div>
    </header>
  );
}

function UserChip() {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const email = useAuthStore((s) => s.email);
  const { logout, isPending } = useLogout();
  if (!userId) {
    return (
      <Link
        to="/login"
        className="text-primary underline-offset-4 hover:underline"
      >
        {t("nav.login")}
      </Link>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground" data-testid="current-user">
        {email ?? ""} · {role ? t(roleLabelKey(role)) : ""}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={logout}
        disabled={isPending}
      >
        {t("nav.logout")}
      </Button>
    </div>
  );
}

function Sidebar() {
  const { t } = useTranslation();
  const canClientCrud = useCan("client_crud");
  const canMeetingTypeCrud = useCan("meeting_type_crud");
  const canListAnalysisView = useCan("list_analysis_view");
  const canAnalysisTemplateEdit = useCan("analysis_template_edit");
  // TM-28f9536d — error console is admin-only; gated by its own action so
  // the manager (who can see Clients/Employees) no longer sees «Ошибки».
  const canErrorView = useCan("error_view");
  return (
    <aside className="hidden w-56 shrink-0 md:block" aria-label="primary">
      <nav className="flex flex-col gap-1 text-sm">
        <SideLink to="/meetings" label={t("nav.meetings")} />
        {canClientCrud ? (
          <>
            <SideLink to="/clients" label={t("nav.clients")} />
            <SideLink to="/employees" label={t("nav.employees")} />
          </>
        ) : null}
        {canListAnalysisView ? (
          <SideLink to="/analyses" label={t("nav.analyses")} />
        ) : null}
        {canMeetingTypeCrud ? (
          <div className="mt-4 flex flex-col gap-1">
            <p className="px-2 text-xs uppercase tracking-wide text-muted-foreground">
              {t("nav.adminSection")}
            </p>
            <SideLink
              to="/admin/meeting-types"
              label={t("nav.adminMeetingTypes")}
            />
            <SideLink to="/admin/api-keys" label={t("nav.adminApiKeys")} />
            <SideLink to="/admin/settings" label={t("nav.adminSettings")} />
            <SideLink to="/admin/users" label={t("nav.adminUsers")} />
            <SideLink
              to="/admin/custom-fields"
              label={t("nav.adminCustomFields")}
            />
            <SideLink to="/admin/folders" label={t("nav.adminFolders")} />
            {canErrorView ? (
              <SideLink to="/admin/errors" label={t("nav.adminErrors")} />
            ) : null}
            {canAnalysisTemplateEdit ? (
              <SideLink
                to="/admin/analysis-templates"
                label={t("nav.adminAnalysisTemplates")}
              />
            ) : null}
          </div>
        ) : null}
      </nav>
    </aside>
  );
}

interface SideLinkProps {
  to: string;
  label: string;
}

function SideLink({ to, label }: SideLinkProps) {
  return (
    <Link
      to={to}
      className="rounded-md px-2 py-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
      activeProps={{ className: "bg-accent text-foreground" }}
    >
      {label}
    </Link>
  );
}

function roleLabelKey(role: Role): string {
  if (role === "admin") return "shell.roleAdmin";
  if (role === "manager") return "shell.roleManager";
  return "shell.roleUser";
}
